import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, Lightbulb } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ServiceFAQ } from '@/components/sections/ServiceFAQ';
import { useJsonLd } from '@/hooks/useJsonLd';

const consultingFAQs = [
  { q: "What does a Part-Time CFO do for a small business?", a: "A Part-Time CFO provides senior financial leadership without a full-time salary. This includes cash flow forecasting, budgeting, financial statement analysis, KPI tracking, strategic planning, and bank/investor communication. KGOB's CFO advisory clients typically have $500K-$10M+ in revenue and need more than bookkeeping but aren't ready for a $150K-$250K full-time hire." },
  { q: "How much does a fractional CFO cost in Charlotte, NC?", a: "Part-Time CFO services are typically structured as monthly retainers based on scope and hours needed. Costs vary by business complexity, but most small to mid-size businesses invest a fraction of what a full-time CFO would cost while getting strategic guidance tailored to their growth stage. KGOB serves clients from Charlotte, Winston-Salem, Mooresville, and remotely nationwide." },
  { q: "What's the difference between a CPA and a financial advisor?", a: "A CPA is licensed to prepare tax returns, audit financial statements, and represent you before the IRS. A financial advisor typically manages investments and retirement planning. KGOB operates as a strategic CPA firm — we handle tax, accounting, compliance, and business advisory, but we don't manage investment portfolios. Many clients work with both a CPA and a financial advisor." },
  { q: "Can KGOB help with business succession planning?", a: "Yes. Succession planning involves valuing the business, structuring the transition (sale, gift, or family transfer), minimizing transfer taxes, and ensuring operational continuity. KGOB handles the tax and financial structuring side — entity restructuring, buy-sell agreements, estate planning coordination, and valuation — often in collaboration with your attorney." },
  { q: "How do I know if my business needs strategic consulting vs. just accounting?", a: "If you're asking questions like 'Should I hire or outsource?', 'Can I afford to expand?', 'What's my breakeven?', 'How do I prepare for a sale in 3-5 years?' — you need strategic consulting, not just bookkeeping. KGOB's advisory services bridge the gap between knowing your numbers and knowing what to do with them." }
];

const StrategicConsulting = () => {
    useJsonLd('consulting-0', {"@context": "https://schema.org", "@type": "Service", "name": "Strategic Business Consulting", "description": "Senior-level financial guidance without the full-time salary. Cash flow forecasting, budgeting, KPI tracking, financial analysis, and strategic planning for growing businesses.", "url": "https://kgob.com/services/consulting", "provider": {"@type": "AccountingService", "name": "KGOB CPAs & Advisors", "url": "https://kgob.com", "address": {"@type": "PostalAddress", "addressLocality": "Charlotte", "addressRegion": "NC", "addressCountry": "US"}}, "serviceType": ["Fractional CFO", "Business Advisory", "Financial Planning", "Cash Flow Management", "Strategic Consulting"], "areaServed": {"@type": "Country", "name": "United States"}});

  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      <Helmet>
        <title>Strategic Business Consulting | Fractional CFO & Advisory | KGOB</title>
        <meta name="description" content="Strategic business consulting and advisory when the numbers need to drive the decision, including fractional CFO perspective for owners facing major choices." />
        <link rel="canonical" href="https://kgob.com/services/consulting" />
        <meta property="og:title" content="Strategic Business Consulting | Fractional CFO & Advisory | KGOB" />
        <meta property="og:description" content="Fractional CFO services, business advisory, cash flow management, and strategic financial planning for growing businesses." />
        <meta property="og:url" content="https://kgob.com/services/consulting" />
        <meta property="og:type" content="website" />
      </Helmet>
      
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-b from-[#0B2240] via-[#0f2d4f] to-[#0B2240] py-20 lg:py-32">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-4xl mx-auto text-center"
            >
              <div className="flex justify-center mb-6">
                <Lightbulb className="w-16 h-16 text-[#D4AF37]" />
              </div>
              
              <h1 className="font-['Playfair_Display'] text-5xl font-bold text-[#F7F6F2] mb-6">
                Strategic Business Consulting
              </h1>
              
              <p className="text-xl text-[#D4AF37] italic mb-4">
                When the numbers need to drive the decision.
              </p>
              
              <p className="text-lg text-[#F7F6F2]/90 mb-6">
                KGOB strategic business consulting gives owners a calm, financially grounded view on major decisions. Our business consulting services connect accounting insight with planning, profitability, and succession questions.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
                <Button data-testid="strategic-consulting-button-4" asChild className="h-14 px-8 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]">
                  <Link data-testid="strategic-consulting-link-4" to="/book-consultation?source=services-consulting">Request Strategy Session</Link>
                </Button>
                <Button data-testid="strategic-consulting-button-3" asChild variant="outline" className="h-14 px-8 border-2 border-[#F7F6F2] text-[#F7F6F2]">
                  <Link data-testid="strategic-consulting-link-3" to="/services">View All Services</Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Keyword focus */}
        <section className="py-16 bg-white" data-testid="consulting-keyword-section">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl text-center">
            <h2 className="font-['Playfair_Display'] text-3xl font-semibold text-[#0B2240] mb-4">
              Strategic business consulting for major decisions
            </h2>
            <p className="text-lg text-[#4C5A70] max-w-3xl mx-auto">
              Fractional CFO perspective, cash flow modeling, and succession planning for owners who want the numbers to lead the conversation.
            </p>
          </div>
        </section>

        {/* Is This Right for You? */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-8 text-center">
              Is This Right for You?
            </h2>
            
            <Card className="p-8 border-2 border-[#E1DFD7]">
              <p className="text-[#4C5A70] mb-6">
                Our strategic consulting is designed for business owners and leaders facing important decisions that require financial insight. You will benefit most if:
              </p>
              
              <div className="space-y-3">
                {[
                  'You are considering a major decision (expansion, acquisition, new product line)',
                  'You need help modeling scenarios or projecting cash flow',
                  'You want a CPA\'s perspective on a tax, structure, or valuation question',
                  'You are preparing for a merger, acquisition, or sale',
                  'You need coordination between your CPA, attorney, and other advisors'
                ].map((item, i) => (
                  <div key={i} className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <span className="text-[#4C5A70]">{item}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </section>

        {/* What You Get */}
        <section className="py-20 bg-[#F7F6F2]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-12 text-center">
              What You Get
            </h2>
            
            <div className="space-y-8">
              <Card className="p-8 border-2 border-[#E1DFD7]">
                <h3 className="font-['Playfair_Display'] text-2xl text-[#0B2240] mb-4">Focused Consultations</h3>
                <p className="text-[#4C5A70] mb-4">
                  We provide one-on-one consultations focused on your specific question, whether it is a tax strategy, business decision, or financial modeling need.
                </p>
                <ul className="space-y-2">
                  {[
                    '1-2 hour focused sessions',
                    'Pre-meeting preparation and research',
                    'Follow-up summary and action steps',
                    'Coordination with your other advisors as needed'
                  ].map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <CheckCircle className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" />
                      <span className="text-sm text-[#4C5A70]">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>

              <Card className="p-8 border-2 border-[#E1DFD7]">
                <h3 className="font-['Playfair_Display'] text-2xl text-[#0B2240] mb-4">Mergers, Acquisitions & Expansion</h3>
                <p className="text-[#4C5A70] mb-4">
                  We help you evaluate opportunities, model scenarios, and coordinate due diligence for major transactions.
                </p>
                <ul className="space-y-2">
                  {[
                    'Buy-side and sell-side financial review',
                    'Deal structure and tax optimization',
                    'Cash flow and profitability modeling',
                    'Post-transaction integration planning'
                  ].map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <CheckCircle className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" />
                      <span className="text-sm text-[#4C5A70]">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>

              <Card className="p-8 border-2 border-[#E1DFD7]">
                <h3 className="font-['Playfair_Display'] text-2xl text-[#0B2240] mb-4">Scenario Modeling & Projections</h3>
                <p className="text-[#4C5A70] mb-4">
                  We build financial models to help you compare options, project outcomes, and make data-driven decisions.
                </p>
                <ul className="space-y-2">
                  {[
                    'Cash flow projections (12-36 months)',
                    'Profitability and break-even analysis',
                    'What-if scenario modeling',
                    'Pricing and margin optimization'
                  ].map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <CheckCircle className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" />
                      <span className="text-sm text-[#4C5A70]">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-12 text-center">
              How It Works
            </h2>
            
            <div className="space-y-6">
              {[
                { step: '1', title: 'Initial Inquiry', desc: 'You share your question or decision via consultation request. We review and schedule a time.' },
                { step: '2', title: 'Preparation', desc: 'We review your financials, research your question, and prepare for a focused, productive session.' },
                { step: '3', title: 'Strategy Session', desc: 'We meet to discuss your question, model scenarios, and develop a recommendation or action plan.' },
                { step: '4', title: 'Follow-Up Summary', desc: 'We provide a written summary of our conversation, key takeaways, and next steps.' },
                { step: '5', title: 'Ongoing Support', desc: 'If needed, we stay engaged as you implement the strategy and coordinate with other advisors.' }
              ].map((item, i) => (
                <Card key={i} className="p-6 border-2 border-[#E1DFD7]">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] flex items-center justify-center">
                      <span className="text-[#0B2240] font-bold text-lg">{item.step}</span>
                    </div>
                    <div>
                      <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-2">{item.title}</h3>
                      <p className="text-[#4C5A70]">{item.desc}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What Changes */}
        <section className="py-20 bg-[#F7F6F2]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-12 text-center">
              What Changes
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="p-6 border-2 border-[#E1DFD7] text-center">
                <div className="mb-4">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-[#0B2240]" />
                  </div>
                </div>
                <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-3">Functional</h3>
                <p className="text-[#4C5A70] text-sm">
                  You have a clear, financially grounded plan for your decision with projections and action steps.
                </p>
              </Card>

              <Card className="p-6 border-2 border-[#E1DFD7] text-center">
                <div className="mb-4">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-[#0B2240]" />
                  </div>
                </div>
                <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-3">Emotional</h3>
                <p className="text-[#4C5A70] text-sm">
                  You feel confident knowing you have a CPA\'s perspective backing your decision.
                </p>
              </Card>

              <Card className="p-6 border-2 border-[#E1DFD7] text-center">
                <div className="mb-4">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-[#0B2240]" />
                  </div>
                </div>
                <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-3">Strategic</h3>
                <p className="text-[#4C5A70] text-sm">
                  With clarity on your options, you can move forward with confidence on major business decisions.
                </p>
              </Card>
            </div>
          </div>
        </section>

        <ServiceFAQ faqs={consultingFAQs} />

        {/* Final CTA */}
        <section className="py-20 bg-gradient-to-b from-[#0B2240] to-[#0f2d4f]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
            <h2 className="font-['Playfair_Display'] text-4xl font-bold text-[#F7F6F2] mb-6">
              Facing a Big Decision?
            </h2>
            <p className="text-lg text-[#F7F6F2]/90 mb-10">
              Schedule a strategy session to discuss your question. We will help you think through your options and develop a clear path forward.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button data-testid="strategic-consulting-button-2" asChild className="h-14 px-8 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]">
                <Link data-testid="strategic-consulting-link-2" to="/book-consultation?source=services-consulting">Request Strategy Session</Link>
              </Button>
              <Button data-testid="strategic-consulting-button-1" asChild variant="outline" className="h-14 px-8 border-2 border-[#F7F6F2] text-[#F7F6F2]">
                <Link data-testid="strategic-consulting-link-1" to="/services">View All Services</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default StrategicConsulting;